import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { DynamicTableComponent } from 'app/shared/components/dynamic-table/dynamic-table.component';
import { DOCUMENT_IMPERSONATE_USER_TABLE_STATE, IMPERSONATE_USER_TABLE_CONFIG } from './impersonate-user-table.config';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { ImpersonateService } from './impersonate.service';
import { finalize, take } from 'rxjs';
import { DataTableColumnFiltersApi, DataTableColumnModel, DataTableConfigSettings, DataTableStateModel } from 'app/shared/components/dynamic-table/dynamic-table.model';
import { IUser } from 'app/shared/models/users.model';
import { HelperService } from 'app/shared/services/helper.service';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';

@Component({
  selector: 'app-impersonate-user',
  standalone: true,
  imports: [DynamicTableComponent, CommonModule, FormsModule, TranslateModule, NgxDatatableModule],
  templateUrl: './impersonate-user.component.html',
  styleUrl: './impersonate-user.component.scss'
})
export class ImpersonateUserComponent {
  tableConfig = IMPERSONATE_USER_TABLE_CONFIG;
  users: IUser[] = [];
  columns = this.helperService.deepCopy(IMPERSONATE_USER_TABLE_CONFIG);
  totalCount = 0;
  loading = true;
  error: string | null = null;
  isDataTableReady: boolean = false;
  defaultColumns = this.helperService.deepCopy(IMPERSONATE_USER_TABLE_CONFIG);
  selectedFilterName = '';
  datatableStateFromStorage = this.helperService.getGridFilterObjectFromSessionStorage(DOCUMENT_IMPERSONATE_USER_TABLE_STATE.gridName);
  dataTableState: DataTableStateModel = this.datatableStateFromStorage ? this.datatableStateFromStorage : { ...DOCUMENT_IMPERSONATE_USER_TABLE_STATE };
  filters: DataTableColumnFiltersApi[] = [];

  constructor(
    private modalService: NgbModal,
    private impersonateService: ImpersonateService,
    private translate: TranslateService,
    private helperService: HelperService,
    private configurationService: ConfigurationService,
    private toastr: ToastrService,
  ) { }

  ngOnInit(): void {
    this.getDataTableConfiguration();
  }

  getDataTableConfiguration() {
    this.configurationService.getInitDataTableConfiguration(this.dataTableState.gridName)
      .pipe(
        take(1),
        finalize(() => {
          this.fetchTableData();
        })
      )
      .subscribe((res: DataTableConfigSettings) => {
        if (this.datatableStateFromStorage) {
          this.columns = this.datatableStateFromStorage.datatableColumns;
          this.filters = this.datatableStateFromStorage.datatableFilters ?? [];
        } else {
          if (res && res.gridColumnSettings && !!JSON.parse(res.gridColumnSettings).length) {
            this.columns = JSON.parse(res.gridColumnSettings);
            this.dataTableState.datatableColumns = JSON.parse(res.gridColumnSettings);
          }

          if (res && res.gridFilterSettings && !!JSON.parse(res.gridFilterSettings).length) {
            this.filters = JSON.parse(res.gridFilterSettings);
            this.dataTableState.datatableFilters = JSON.parse(res.gridFilterSettings);
          }
        }

        if (res && res.name) {
          this.selectedFilterName = res.name;
        }

      });
  }

  fetchTableData(): void {
    this.error = null;

    this.impersonateService.getUsers(this.dataTableState).pipe(take(1)).subscribe({
      next: (response: any) => {
        this.users = response?.items || [];
        this.totalCount = response?.totalCount || 0;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'Error loading users.';
      }
    });
  }

  getDataTableState(data: DataTableStateModel): void {
    this.dataTableState = data;
    this.fetchTableData();
  }

  onActionButtonClick(data: any): void {
    if (data.action === 'impersonate') {
      this.openImpersonateModal(data.row);
    }
  }

  openImpersonateModal(user: any): void {
    const modalRef = this.modalService.open(ConfirmationDialogComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'confirmation-modal',
    });

    modalRef.componentInstance.title = this.translate.instant('label.confirm_impersonation');
    modalRef.componentInstance.message = this.translate.instant('messages.are_you_sure_impersonate_user_message', { userName: user.fullName });
    modalRef.componentInstance.notConfirmActionLabel = this.translate.instant('buttons.no');
    modalRef.componentInstance.confirmActionLabel = this.translate.instant('buttons.yes');

    modalRef.result.then(
      (result) => {
        if (result) {
          this.impersonateUser(user);
        }
      },
    );
  }

  impersonateUser(user: IUser): void {
    this.impersonateService.startImpersonation(user);
  }

  onSaveDatatableSettings(data: DataTableStateModel) {
    const dataForSave = this.helperService.deepCopy(data);

    if (dataForSave && dataForSave.datatableColumns) {
      dataForSave.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dataTableState.gridName,
      name: dataForSave.datatableFilterName || '',
      isGlobal: dataForSave.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForSave.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForSave.datatableColumns) || ''
    };


    this.configurationService.setDataTableConfiguration(params)
      .pipe(take(1))
      .subscribe();
  }

  onUpdateDatatableSettings(data: DataTableStateModel) {
    const dataForUpdate = this.helperService.deepCopy(data);

    if (dataForUpdate && dataForUpdate.datatableColumns) {
      dataForUpdate.datatableColumns.forEach((datatableColumn: DataTableColumnModel) => {
        datatableColumn.dataTableColumnFilters = [];
      });
    }

    const params = {
      gridType: this.dataTableState.gridName,
      name: dataForUpdate.datatableFilterName || '',
      isGlobal: dataForUpdate.datatableFilterIsGlobal || false,
      gridFilterSettings: JSON.stringify(dataForUpdate.datatableFilters) || '',
      gridColumnSettings: JSON.stringify(dataForUpdate.datatableColumns) || ''
    };

    const filterId = dataForUpdate.datatableFilterId;

    this.configurationService.updateSavedFilter(filterId, params)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastr.show(this.translate.instant('messages.filter_successfully_updated'), ToastTypesEnum.SUCCESS);
        },
        error: (err) => {
          this.toastr.show(this.translate.instant('messages.failed_to_update_filter'), ToastTypesEnum.ERROR);
          console.error(err);
        }
      });
  }
}
